import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  CreditCard, Check, Crown, Zap, Star, BarChart3, Globe,
  Rss, Sparkles, ArrowRight
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planIcons = { free: Star, starter: Zap, pro: Crown, enterprise: CreditCard };
const planColors = {
  free: "border-slate-200 dark:border-slate-700",
  starter: "border-blue-200 dark:border-blue-800",
  pro: "border-primary",
  enterprise: "border-amber-300 dark:border-amber-700",
};

export default function Subscription() {
  const { t } = useLanguage();
  const [plans, setPlans] = useState({});
  const [current, setCurrent] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, currentRes, usageRes] = await Promise.all([
        axios.get(`${API}/subscription/plans`),
        axios.get(`${API}/subscription/current`),
        axios.get(`${API}/subscription/usage`),
      ]);
      setPlans(plansRes.data);
      setCurrent(currentRes.data);
      setUsage(usageRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleChangePlan = async (plan) => {
    setChanging(plan);
    try {
      await axios.post(`${API}/subscription/change`, { plan });
      toast.success(`${t("sub.planChanged")}: ${plans[plan]?.name}`);
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
    finally { setChanging(null); }
  };

  const usageItems = usage ? [
    { key: "sites", label: t("sub.sites"), icon: Globe },
    { key: "posts_month", label: t("sub.postsMonth"), icon: BarChart3 },
    { key: "ai_calls_month", label: t("sub.aiCalls"), icon: Sparkles },
    { key: "rss_feeds", label: t("sub.rssFeeds"), icon: Rss },
  ] : [];

  return (
    <div data-testid="subscription-page" className="space-y-6">
      <div>
        <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("sub.title")}</h1>
        <p className="text-sm text-muted-foreground mt-1 font-body">{t("sub.subtitle")}</p>
      </div>

      {/* Aktualny plan + usage */}
      {current && usage && (
        <Card className="rounded-xl border shadow-sm bg-card border-primary/30">
          <CardHeader className="pb-2 pt-4 px-5">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-heading flex items-center gap-2">
                <CreditCard className="w-4 h-4 text-primary" strokeWidth={1.5} />
                {t("sub.currentPlan")}
              </CardTitle>
              <Badge className="bg-primary/10 text-primary border-0 rounded-md text-xs font-bold">{current.plan_details?.name}</Badge>
            </div>
          </CardHeader>
          <CardContent className="px-5 pb-5 pt-2">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {usageItems.map((item) => {
                const u = usage.usage[item.key];
                if (!u) return null;
                return (
                  <div key={item.key} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <item.icon className="w-3 h-3" strokeWidth={1.5} />{item.label}
                      </span>
                      <span className="text-xs font-semibold text-foreground">{u.used}/{u.limit}</span>
                    </div>
                    <Progress value={u.pct} className={`h-2 ${u.pct > 80 ? "[&>div]:bg-red-500" : u.pct > 50 ? "[&>div]:bg-amber-500" : ""}`} />
                  </div>
                );
              })}
            </div>
            {current.expires_at && (
              <p className="text-[10px] text-muted-foreground mt-3">
                {t("sub.expiresAt")}: {new Date(current.expires_at).toLocaleDateString()}
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Plany */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {Object.entries(plans).map(([key, plan]) => {
          const Icon = planIcons[key] || Star;
          const isCurrentPlan = current?.plan === key;
          return (
            <Card key={key} data-testid={`plan-${key}`}
              className={`rounded-xl border-2 shadow-sm transition-all ${isCurrentPlan ? `${planColors[key]} shadow-md` : "border-border hover:border-primary/20"}`}>
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Icon className={`w-5 h-5 ${isCurrentPlan ? "text-primary" : "text-muted-foreground"}`} strokeWidth={1.5} />
                  <h3 className="text-base font-heading font-bold text-foreground">{plan.name}</h3>
                </div>

                <div className="mb-4">
                  <span className="text-3xl font-bold font-heading text-foreground">{plan.price}</span>
                  <span className="text-sm text-muted-foreground ml-1">{plan.currency}/{t("sub.month")}</span>
                </div>

                <Separator className="mb-4" />

                <div className="space-y-2 mb-5">
                  {plan.features?.map((f, i) => (
                    <div key={i} className="flex items-start gap-2">
                      <Check className="w-3.5 h-3.5 text-emerald-500 mt-0.5 shrink-0" strokeWidth={2} />
                      <span className="text-xs text-foreground/80">{f}</span>
                    </div>
                  ))}
                </div>

                <div className="text-[10px] text-muted-foreground space-y-0.5 mb-4">
                  <p>{plan.limits?.sites} {t("sub.sites")} | {plan.limits?.ai_calls_month} AI/mc</p>
                  <p>{plan.limits?.posts_month} {t("sub.postsMonth")} | {plan.limits?.rss_feeds} RSS</p>
                </div>

                {isCurrentPlan ? (
                  <Button variant="outline" className="w-full h-9 text-xs rounded-lg" disabled>
                    <Check className="w-3.5 h-3.5 mr-1" /> {t("sub.currentPlanBtn")}
                  </Button>
                ) : (
                  <Button className="w-full h-9 text-xs rounded-lg gap-1"
                    data-testid={`select-plan-${key}`}
                    onClick={() => handleChangePlan(key)}
                    disabled={changing === key}
                    variant={key === "pro" ? "default" : "outline"}>
                    {changing === key ? "..." : (
                      <>{key === "free" ? t("sub.downgrade") : t("sub.upgrade")} <ArrowRight className="w-3 h-3" /></>
                    )}
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <p className="text-[10px] text-muted-foreground/50 text-center">
        {t("sub.stripeNote")}
      </p>
    </div>
  );
}
